/**
 * The work queue: patients whose results are ready and who have no plan yet.
 *
 * Spec, 17 September: "The clinician dashboard should not display the entire
 * treatment-selection interface for every patient. The main dashboard is a
 * work queue. New patients whose reports are ready should appear as compact,
 * actionable alerts."
 *
 * So each row is deliberately small: who, what they want, how long they have
 * been waiting, and one way in. Everything else about the patient is one click
 * away and does not belong on a queue.
 *
 * A patient leaves this list the moment a plan is released, which is the only
 * thing that stops a queue turning into a second patient list.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingRows,
  PageHeader,
  Pill,
} from '../components/ui';
import { initials } from '../lib/format';

interface QueueEntry {
  patientId: string;
  firstName: string;
  lastName: string;
  primaryGoal: string | null;
  goalTargetValue: number | null;
  goalTargetUnit: string | null;
  phenotypes: string[];
  labsReadyDaysAgo: number;
  /** The goal as the dashboard shows it; an HRT / TRT row carries the intake's hormone goal here. */
  goalHeadline?: string | null;
  /** 'weight' and / or 'hrt': the programs the row is waiting in (GET /regimens/queue). */
  programs?: string[];
}

function goalLine(entry: QueueEntry): string {
  if (!entry.primaryGoal) return entry.goalHeadline ?? 'No goal recorded';
  const head = entry.primaryGoal.charAt(0).toUpperCase() + entry.primaryGoal.slice(1);
  if (entry.goalTargetValue === null) return head;
  return `${head}, target ${entry.goalTargetValue}${entry.goalTargetUnit ? ` ${entry.goalTargetUnit}` : ''}`;
}

/** `insulin_resistance` reads as "Insulin resistance" on a chip. */
function phenotypeLabel(tag: string): string {
  const words = tag.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function waitingLabel(days: number): string {
  if (days <= 0) return 'Ready today';
  if (days === 1) return 'Waiting 1 day';
  return `Waiting ${days} days`;
}

export function NewPatientsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['treatment-queue'],
    queryFn: () => api.get<{ queue: QueueEntry[] }>('/regimens/queue'),
  });

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;
  if (isLoading) return <LoadingRows rows={6} />;

  const queue = data?.queue ?? [];

  return (
    <>
      <PageHeader
        title="New patients"
        subtitle={
          queue.length === 0
            ? 'Nobody is waiting for a treatment decision.'
            : `${queue.length} ${queue.length === 1 ? 'patient is' : 'patients are'} waiting for a treatment decision.`
        }
      />

      <Card padded={false}>
        {queue.length === 0 ? (
          <EmptyState
            title="Nothing waiting"
            body="A patient appears here once their report is ready and no treatment has been chosen."
          />
        ) : (
          <ul className="divide-y divide-ink/5">
            {queue.map((entry) => (
              <li key={entry.patientId}
                className="flex flex-wrap items-center gap-4 px-5 py-4 hover:bg-ink/[0.02]">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink/5 text-xs font-semibold text-ink-muted">
                  {initials(entry)}
                </span>

                <span className="min-w-0 flex-1">
                  <Link to={`/patients/${entry.patientId}`}
                    className="font-semibold text-ink hover:underline">
                    {entry.firstName} {entry.lastName}
                  </Link>
                  <span className="block text-sm text-ink-muted">{goalLine(entry)}</span>
                  {entry.programs?.includes('hrt') && (
                    <span
                      className="mt-1 inline-block whitespace-nowrap rounded-[4px] bg-plum-soft px-1.5 py-[3px] text-[10.5px] font-semibold uppercase leading-none tracking-[0.03em] text-plum"
                      data-testid="row-hrt-chip"
                    >
                      HRT / TRT
                    </span>
                  )}
                  {entry.phenotypes.length > 0 && (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {entry.phenotypes.slice(0, 4).map((tag) => (
                        <Pill key={tag} className="bg-ink/5 text-ink-muted">
                          {phenotypeLabel(tag)}
                        </Pill>
                      ))}
                    </span>
                  )}
                </span>

                {/* The wait is the only number on the row, because it is the
                    only one that decides who a doctor should open first. */}
                <span className="text-xs text-ink-muted whitespace-nowrap">
                  {waitingLabel(entry.labsReadyDaysAgo)}
                </span>

                <Link to={`/patients/${entry.patientId}/workspace`}>
                  <Button size="sm">{entry.programs && !entry.programs.includes('weight') ? 'Open HRT / TRT plan' : 'View MCOS treatment plan'}</Button>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
